"use client";

import {
  ColumnDef,
  FilterFn,
  flexRender,
  SortingFn,
  sortingFns,
  useReactTable,
  TableOptions,
  SortingFns,
  FilterFns,
  RowData,
} from "@tanstack/react-table";

import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";
import { useEffect, useMemo } from "react";
import { cn } from "@/lib/utils";
import {
  compareItems,
  RankingInfo,
  rankItem,
} from "@tanstack/match-sorter-utils";
import { Skeleton } from "../skeleton/skeleton";

declare module "@tanstack/react-table" {
  //add fuzzy filter to the filterFns
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
  }
  interface SortingFns {
    fuzzy: SortingFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

type Nestable = { [key: string]: any; children?: Nestable[] };

export type DataTableOptions<TData extends RowData> = Omit<
  TableOptions<TData>,
  "sortingFns" | "filterFns"
> & {
  sortingFns?: SortingFns;
  filterFns?: FilterFns;
};

export interface DataTableProps<TData extends Nestable>
  extends DataTableOptions<TData> {
  noResults: React.ReactNode;
  filter?: string;
  loading?: boolean;
  className?: string;
}

export function DataTable<TData extends Nestable, TValue>({
  noResults,
  filter,
  loading,
  columns,
  className,
  data,
  filterFns,
  sortingFns,
  ...opts
}: DataTableProps<TData>) {
  const cols = useMemo(() => {
    if (!loading) return columns;
    return columns.map((col) => {
      return {
        ...col,
        cell: () => <Skeleton className="w-full h-1/2" />,
      } satisfies ColumnDef<TData, TValue>;
    });
  }, [loading, columns]);

  const rows = useMemo(() => {
    if (!loading) return data;
    return Array.from({ length: 12 }, () => ({})) as TData[];
  }, [loading, data]);

  const table = useReactTable({
    ...opts,
    data: rows,
    columns: cols,
    filterFns: { ...filterFns, fuzzy: fuzzyFilter },
    sortingFns: { ...sortingFns, fuzzy: fuzzySort },
  });

  useEffect(() => {
    // collapse all rows when filter is cleared (?)
    if (!filter) table.resetExpanded(false);
  }, [filter]);

  return (
    <div
      className={cn(
        "rounded-md border border-input overflow-auto max-h-full no-scrollbar",
        className
      )}
    >
      <table className="relative w-full max-h-full caption-bottom text-sm overflow-auto table-fixed min-w-[700px]">
        <TableHeader className="sticky top-0 z-10">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      width:
                        header.getSize() === 0
                          ? "auto"
                          : `${header.getSize()}px`,
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={cn("flex items-center gap-x-1 text-base ", {
                          "cursor-pointer select-none":
                            header.column.getCanSort(),
                        })}
                        onClick={header.column.getToggleSortingHandler()}
                        title={
                          header.column.getCanSort()
                            ? header.column.getNextSortingOrder() === "asc"
                              ? "Sort ascending"
                              : header.column.getNextSortingOrder() === "desc"
                                ? "Sort descending"
                                : "Unsort"
                            : undefined
                        }
                      >
                        <div className="truncate">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                        <span className="w-[3px]">
                          {{
                            asc: "↑",
                            desc: "↓",
                          }[header.column.getIsSorted() as string] ?? null}
                        </span>
                      </div>
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                onClick={row.getToggleExpandedHandler()}
                data-state={row.getIsSelected() && "selected"}
                className={cn({
                  "bg-primary-muted border-b-0":
                    row.getCanExpand() && row.getIsExpanded(),
                  "font-bold": row.getCanExpand(),
                })}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      "text-lg",
                      cell.row.getCanExpand() && "cursor-pointer"
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                {noResults}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </table>
    </div>
  );
}

export const fuzzyFilter: FilterFn<any> = (row, colId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(colId), value);
  addMeta({ itemRank });

  const ok = itemRank.passed;
  if (!ok) return ok;

  // NOTE: uncertain where this logic should go as there is no real mention (that i can find)
  // of how to handle this when dealing with searching on nested rows.
  const parent = row.getParentRow();
  if (parent && parent.getCanExpand() && !parent.getIsExpanded()) {
    // expand parent row if child row is matched
    parent.toggleExpanded(true);
    return ok;
  }

  return ok;
};

export const fuzzySort: SortingFn<any> = (rowA, rowB, columnId) => {
  let dir = 0;
  if (rowA.columnFiltersMeta[columnId]) {
    dir = compareItems(
      rowA.columnFiltersMeta[columnId]?.itemRank!,
      rowB.columnFiltersMeta[columnId]?.itemRank!
    );
  }
  return dir === 0 ? sortingFns.alphanumeric(rowA, rowB, columnId) : dir;
};
